/* eslint-disable max-statements */

import dayjs from "dayjs";
import { ChangeEvent, KeyboardEvent, useCallback, useEffect, useState } from "react";
import { ActionFunctionArgs, LoaderFunctionArgs, useFetcher, useLoaderData, useNavigate, useParams } from "react-router-dom";
import { packObjForSubmit, unpackObjForSubmit } from "../../utils";
import { doFetch } from "../../utils/doFetch";
import { doFetchPut } from "../../utils/doFetchPut";
import { IDOAShiftsData } from "../availability-administration/AvailabilityAdministrationData";
import { IDOAEmployeeData } from "./AvailabilityAdministrationData";
import { AvailabilityAdministrationEmployee } from "./AvailabilityAdministrationEmployee";
import { AvailableCode, SUCCESS_MSG } from "./AvailabilityAdministrationModel";

const fetchShift = async (request: any, dispatch: string, date: string) => {
  const shiftsData: IDOAShiftsData = await doFetch(`doa/shifts`, { signal: request.signal }, true, true);
  return shiftsData.shifts.find(s => s.shift === dispatch && s.dispatchDate === dayjs(date, 'DDMMYYYY').format('DD.MM.YYYY'));
}

export const availabilityAdministrationEmployeeRouteLoader = async ({
  request,
  params
}: LoaderFunctionArgs) => {
  let shift = null;
  let employee = null;
  if (params.dispatch && params.date) {
    shift = await fetchShift(request, params.dispatch, params.date);
  }
  if (params.regId && params.dispatch && params.date) {
    employee = await doFetch(`doa/${params.dispatch}/${params.date}/${params.regId}`, { signal: request.signal }, true, true);
  }
  return {
    shift,
    employee
  }
}

export const availabilityAdministrationEmployeeRouteAction = async ({
  request,
  params
}: ActionFunctionArgs) => {
  let formData = await request.formData();
  let obj = JSON.parse(unpackObjForSubmit(formData));
  return doFetchPut(
    `doa/${params.dispatch}/${params.date}/${params.regId}/${obj.doaSelection}`,
    { signal: request.signal }
  );
}

export const AvailabilityAdministrationEmployeeRoute = () => {
  const loaderData = useLoaderData() as IDOAEmployeeData;
  const fetcher = useFetcher();
  const params = useParams();
  const navigate = useNavigate();
  const data = fetcher.data as IDOAEmployeeData ?? loaderData;
  const [regId, setRegId] = useState(data.employee?.empInfo?.regId ? `${data.employee?.empInfo?.regId}` : '');
  const [selectedRegId, setSelectedRegId] = useState('');
  const [openPlateModal, setOpenPlateModal] = useState(false);
  const isExpired = data.employee?.expiredShift;
  const isDispatchTypeSix = data.shift?.shift === '1745';
  const isNightDispatch = data.shift?.shift === '1530';

  const [doaSelection, setDoaSelection] = useState(
    data.employee?.empInfo?.doaSelections ?? AvailableCode.NOT_AVAILABLE
  );

  const [actionMsg, setActionMsg] = useState('');

  const onFetchEmployeeData = useCallback(async () => {
    await fetcher.load(`/availability-administration-employee/${params.dispatch}/${params.date}/${selectedRegId}`);
  }, [fetcher, params.dispatch, params.date, selectedRegId]);

  useEffect(() => {
    const success = data.success || data.successResults?.find(sr => !!sr)
    if (success && !params.regId && fetcher.state !== 'loading') {
      onFetchEmployeeData();
      setActionMsg(SUCCESS_MSG);
    } else if (!data.success && fetcher.state !== 'loading' && data.message) {
      setActionMsg(data.message);
    }
  }, [fetcher, regId, params, data, onFetchEmployeeData]);

  useEffect(() => {
    if (data.employee?.empInfo && Object.keys(data.employee.empInfo).length) {
      const { doaSelections, dependentShift } = data.employee.empInfo;
      const hasDependentDoa = dependentShift?.doaSelections;
      let newDoaSelection;
      if (doaSelections && hasDependentDoa) {
        // Both current and dependent shifts have DOA selections
        newDoaSelection = AvailableCode.OPTION_1 | AvailableCode.OPTION_2;
      } else if (!doaSelections && hasDependentDoa) {
        // No current DOA selection, but the dependent shift has one
        newDoaSelection = AvailableCode.OPTION_2;
      } else if (doaSelections && dependentShift) {
        // Only the current shift has a DOA selection
        newDoaSelection = AvailableCode.OPTION_1;
      } else {
        // Default to current DOA selection
        newDoaSelection = doaSelections;
      }
      setDoaSelection(newDoaSelection);
    }
  }, [data.employee?.empInfo, data.employee?.empInfo?.doaSelections]);

  const onSearchEmployee = () => {
    if (data.shift && regId) {
      const date = dayjs(data.shift.dispatchDate, 'DD.MM.YYYY').format('DDMMYYYY');
      fetcher.load(`/availability-administration-employee/${data.shift.shift}/${date}/${regId}`);
      setActionMsg('');
      setSelectedRegId(regId);
    }
  }

  useEffect(() => {
    if (isExpired) navigate('/availability-administration-shifts');
  }, [isExpired]);

  const onSearchEnter = (event: KeyboardEvent<HTMLInputElement>) => {
    event.stopPropagation();
    if (event.key === "Enter") onSearchEmployee();
  }

  const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { target: { value } } = event;
    event.stopPropagation();
    //This regular expression will match strings that contain 0 to 6 digits
    const isValidInput = /^\d{0,6}$/.test(value);
    if (isValidInput) {
      setRegId(value)
    }
  }

  const onMakeMeAvailable = () => {
    if (data.shift && selectedRegId) {
      const date = dayjs(data.shift.dispatchDate, 'DD.MM.YYYY').format('DDMMYYYY');
      const notTypeSixDoaSelection = doaSelection ? 0 : 1;

      fetcher.submit(
        packObjForSubmit({
          doaSelection: isDispatchTypeSix || isNightDispatch ? doaSelection : notTypeSixDoaSelection
        }),
        {
          method: "put",
          action: `/availability-administration-employee/${data.shift.shift}/${date}/${selectedRegId}`,
        }
      );
    }
  }

  const onOpenPlate = () => setOpenPlateModal(prev => !prev)

  const onCheckBoxChange = (code: AvailableCode) => {
    setDoaSelection((prev) => {
      const isFirstOption = code === AvailableCode.OPTION_1;
      if (prev === AvailableCode.OPTION_1 + AvailableCode.OPTION_2 - AvailableCode.AVAILABLE) {
        return isFirstOption ? AvailableCode.OPTION_2 : AvailableCode.OPTION_1
      } else if (!prev) {
        return code
      }
      return prev === code ? AvailableCode.NOT_AVAILABLE :
        AvailableCode.OPTION_1 + AvailableCode.OPTION_2 - AvailableCode.AVAILABLE
    });
  };

  return (
    <AvailabilityAdministrationEmployee
      isDispatchTypeSix={isDispatchTypeSix}
      isNightDispatch={isNightDispatch}
      selectedRegId={selectedRegId}
      regId={regId}
      shift={loaderData.shift}
      onInputChange={onInputChange}
      onSearchEnter={onSearchEnter}
      onSearchEmployee={onSearchEmployee}
      employeeData={data.employee}
      onOpenPlate={onOpenPlate}
      openPlateModal={openPlateModal}
      onMakeMeAvailable={onMakeMeAvailable}
      pendingMakeAvailable={fetcher.state === 'submitting'}
      doaSelection={doaSelection}
      onCheckboxChange={onCheckBoxChange}
      actionMsg={actionMsg}
      onFetchEmployeeData={onFetchEmployeeData}
    />
  )
}